import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { autoUpdate, FloatingPortal, flip, offset, shift, useFloating } from '@floating-ui/react';
import type { EmojiItem } from '@tiptap-pro/extension-emoji';
import cn from 'classnames';

interface EmojiSuggestionListProps {
  items: EmojiItem[];
  command: (attrs: { name: string }) => void;
  clientRect: (() => DOMRect | null) | null;
}

export const EmojiSuggestionList = forwardRef<
  { onKeyDown: (event: KeyboardEvent) => boolean },
  EmojiSuggestionListProps
>(({ items, command, clientRect }, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const { refs, floatingStyles } = useFloating({
    placement: 'bottom-start',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  useEffect(() => {
    refs.setPositionReference({
      getBoundingClientRect: () => clientRect?.() ?? new DOMRect(),
    });
  }, [clientRect, refs]);

  useEffect(() => setSelectedIndex(0), [items]);

  const selectItem = (index: number) => {
    const item = items[index];
    if (item) {
      command({ name: item.name });
    }
  };

  const scrollIntoView = (index: number) => {
    const item = items[index];
    if (item) {
      const node = listRef.current?.querySelector(`[data-emoji-name="${item.name}"]`);
      node?.scrollIntoView({ block: 'nearest' });
    }
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: (event: KeyboardEvent) => {
      if (event.key === 'ArrowUp') {
        const newIndex = (selectedIndex + items.length - 1) % items.length;
        setSelectedIndex(newIndex);
        scrollIntoView(newIndex);
        return true;
      }

      if (event.key === 'ArrowDown') {
        const newIndex = (selectedIndex + 1) % items.length;
        setSelectedIndex(newIndex);
        scrollIntoView(newIndex);
        return true;
      }

      if (event.key === 'Enter') {
        selectItem(selectedIndex);
        return true;
      }

      return false;
    },
  }));

  if (!items.length) return null;

  return (
    <FloatingPortal>
      <div
        ref={refs.setFloating}
        style={floatingStyles}
        className="z-[100] rounded-lg border border-black/10 bg-white shadow-lg"
      >
        <div ref={listRef} className="max-h-72 overflow-y-auto py-1">
          {items.map((item, index) => (
            <button
              key={item.name}
              type="button"
              onClick={() => selectItem(index)}
              data-emoji-name={item.name}
              className={cn('flex w-full items-center gap-2 px-3 py-1.5 text-sm', {
                'bg-black/5': index === selectedIndex,
                'hover:bg-black/5': index !== selectedIndex,
              })}
            >
              <span className="text-lg leading-none">
                {item.fallbackImage ? <img src={item.fallbackImage} className="size-5" alt={item.name} /> : item.emoji}
              </span>
              <span className="truncate text-black/70">:{item.name}:</span>
            </button>
          ))}
        </div>
      </div>
    </FloatingPortal>
  );
});

EmojiSuggestionList.displayName = 'EmojiSuggestionList';
